import React, { useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/teams-app-internal-analytics';
import { LazyIntlProvider } from '@atlassian/teams-common/i18n';

import { VOID_FUNC } from '../../utils/constants';
import { triggerAnalyticsForCloseTeamCreateDialog } from '../analytics';
import { fetchLanguageFile } from '../i18n/utils';
import TeamCreate, { type TeamCreateProps } from '../TeamCreate';

import { type TeamCreateModalProps } from './types';

const DIALOG_WIDTH = 480;

const TeamCreateModal = (props: TeamCreateModalProps) => {
	const { extraAnalyticsAttrs, isBlanketHidden, onClose = VOID_FUNC } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fireEvent } = useAnalyticsEventsNext();

	// Work-around:
	// When TeamCreateDialog renders inside PeopleMenu and the viewport is narrow,
	// the dialog is closed improperly when users click anywhere in the dialog
	const preventCloseDialog = (event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
	};

	const closeDialog = useCallback(() => {
		if (fg('ptc-enable-teams-common-analytics-refactor')) {
			fireEvent('ui.teamCreateDialog.closed', extraAnalyticsAttrs || {});
		} else {
			triggerAnalyticsForCloseTeamCreateDialog(createAnalyticsEvent, extraAnalyticsAttrs);
		}
		onClose();
	}, [createAnalyticsEvent, extraAnalyticsAttrs, onClose, fireEvent]);

	const [isLoading, setLoading] = useState(false);

	const innerSetLoading = useCallback(
		(isLoading: boolean) => {
			setLoading(isLoading);
			(props.setIsLoading || VOID_FUNC)(isLoading);
		},
		[props.setIsLoading],
	);

	const customComponents = {
		...props.customComponents,
		header: props.customComponents?.header || props.customComponents?.modalHeader || ModalHeader,
		title: props.customComponents?.title || props.customComponents?.modalTitle || ModalTitle,
		body: props.customComponents?.body || props.customComponents?.modalBody || ModalBody,
		footer: props.customComponents?.footer || props.customComponents?.modalFooter || ModalFooter,
	};

	const customMessages = {
		...props.customMessages,
		title: props.customMessages?.title || props.customMessages?.modalTitle || undefined,
	};

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, @atlassian/a11y/interactive-element-not-keyboard-focusable
		<div onClick={preventCloseDialog}>
			<ModalDialog
				shouldScrollInViewport
				onClose={closeDialog}
				width={DIALOG_WIDTH}
				isBlanketHidden={isBlanketHidden}
				shouldCloseOnOverlayClick={!isLoading}
				shouldCloseOnEscapePress={!isLoading}
			>
				<TeamCreateContent
					{...props}
					onClose={closeDialog}
					setIsLoading={innerSetLoading}
					customComponents={customComponents}
					customMessages={customMessages}
				/>
			</ModalDialog>
		</div>
	);
};

const TeamCreateContent = (props: TeamCreateProps) => (
	<LazyIntlProvider fetchLanguageFile={fetchLanguageFile}>
		<TeamCreate {...props} />
	</LazyIntlProvider>
);

export default TeamCreateModal;
